import axios from 'axios';
import { toast } from '@/components/ui/use-toast';

// Version checking for cache-busting
const CLIENT_VERSION = __APP_VERSION__;

const createAxiosInstance = (authContext) => {
  const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
    withCredentials: true,
  });

  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedQueue = [];
  };

  axiosInstance.interceptors.request.use(async (config) => {
    const token = localStorage.getItem('accesstoken');
    if (token) {
      config.headers['x-access-token'] = token;
    }
    return config;
  }, (error) => Promise.reject(error));

  axiosInstance.interceptors.response.use(
    (response) => {
      const newToken = response.headers['x-access-token'];
      if (newToken) {
        localStorage.setItem('accesstoken', newToken);
      }

      // Version mismatch detection - triggers reload when server version differs from client
      const serverVersion = response.headers['x-app-version'];
      if (serverVersion && serverVersion !== CLIENT_VERSION) {
        // Prevent reload loops - only reload once per minute
        const lastReload = sessionStorage.getItem('lastVersionReload');
        const now = Date.now();

        if (!lastReload || now - parseInt(lastReload) > 60000) {
          sessionStorage.setItem('lastVersionReload', now.toString());
          toast({
            description: 'New version available, updating...',
          });
          setTimeout(() => window.location.reload(), 2000);
        }
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle network errors
      if (!error.response) {
        console.error('Network error:', error.message);
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Don't intercept the refresh-token endpoint itself — if the refresh token
        // is expired the 401 must reach AuthContext's catch block so it can log out.
        // Without this guard, the interceptor queues the request while waiting for
        // a refresh that's already in progress, creating a deadlock.
        if (originalRequest.url?.includes('/auth/refresh-token')) {
          return Promise.reject(error);
        }

        console.log('Request failed with 401, attempting token refresh');
        
        if (isRefreshing) {
          // Queue the request while refresh is in progress
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            if (token) {
              originalRequest.headers['x-access-token'] = token;
              return axiosInstance(originalRequest);
            }
            return Promise.reject(new Error('Token refresh failed'));
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await authContext.refreshToken();
          if (newToken) {
            originalRequest.headers['x-access-token'] = newToken;
            processQueue(null, newToken);
            return axiosInstance(originalRequest);
          } else {
            throw new Error('No token received from refresh');
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          processQueue(refreshError, null);

          // Only logout if the refresh token was explicitly rejected by the server.
          // Network errors (timeouts, connection refused) should not wipe the session.
          const refreshStatus = refreshError?.response?.status;
          if (refreshStatus === 401 || refreshStatus === 403) {
            setTimeout(() => {
              authContext.logout();
            }, 100);
          }

          return Promise.reject(new Error('UNAUTHORIZED'));
        } finally {
          isRefreshing = false;
        }
      }

      if (error.response?.status === 402) {
        if (authContext.onPaymentRequired) {
          authContext.onPaymentRequired();
        }
        return Promise.reject(new Error('PAYMENT_REQUIRED'));
      }

      if (error.response?.status === 429) {
        setTimeout(() => {
          authContext.logout();
        }, 100);
        return Promise.reject(new Error('RATE_LIMIT_EXCEEDED'));
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};

export default createAxiosInstance;
